import { useEffect, useRef } from 'react';
import { Chart } from 'chart.js';
import { UserPerformanceData } from '../types';

interface ChartRefs {
  statusChartRef: React.RefObject<HTMLCanvasElement | null>;
  typeChartRef: React.RefObject<HTMLCanvasElement | null>;
  pointsChartRef: React.RefObject<HTMLCanvasElement | null>;
  categoryChartRef: React.RefObject<HTMLCanvasElement | null>;
  taskTrendChartRef: React.RefObject<HTMLCanvasElement | null>;
  taskRatioChartRef: React.RefObject<HTMLCanvasElement | null>;
}

interface ChartSelections {
  taskTrend: string;
  taskRatio: string;
}

export const useCharts = (
  performanceData: UserPerformanceData | null,
  chartRefs: ChartRefs,
  chartSelections?: ChartSelections
) => {
  const selections = chartSelections || {
    taskTrend: 'completed-inprogress',
    taskRatio: 'completed-inprogress',
  };
  const statusChartInstance = useRef<Chart | null>(null);
  const typeChartInstance = useRef<Chart | null>(null);
  const pointsChartInstance = useRef<Chart | null>(null);
  const categoryChartInstance = useRef<Chart | null>(null);
  const taskTrendChartInstance = useRef<Chart | null>(null);
  const taskRatioChartInstance = useRef<Chart | null>(null);

  // Initialize Status Distribution Chart
  useEffect(() => {
    if (!performanceData || !chartRefs.statusChartRef.current) return;

    if (statusChartInstance.current) {
      statusChartInstance.current.destroy();
    }

    const ctx = chartRefs.statusChartRef.current.getContext('2d');
    if (!ctx) return;

    const { status_distribution } = performanceData.charts;
    
    statusChartInstance.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Completed', 'In Progress', 'Pending', 'Cancelled', 'On Hold'],
        datasets: [{
          label: 'Tasks by Status',
          data: [
            status_distribution.completed || 0,
            status_distribution.in_progress || 0,
            status_distribution.pending || 0,
            status_distribution.cancelled || 0,
            status_distribution.on_hold || 0,
          ],
          backgroundColor: [
            '#10b981',
            '#3b82f6',
            '#f59e0b',
            '#ef4444',
            '#6b7280',
          ],
          borderColor: '#ffffff',
          borderWidth: 2,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '55%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 12,
              usePointStyle: true,
              pointStyle: 'circle',
              font: {
                size: 10,
                weight: 'bold'
              },
              color: '#374151'
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            cornerRadius: 8,
            padding: 10,
            titleFont: {
              size: 11
            },
            bodyFont: {
              size: 10
            },
            callbacks: {
              label: function(context: any) {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${label}: ${value} (${percentage}%)`;
              }
            }
          }
        },
      },
    });
  }, [performanceData, chartRefs.statusChartRef]);

  // Helper function to get task data based on selection
  const getTaskData = (selection: string, stats: any, statusDist?: any) => {
    const [first, second] = selection.split('-');
    const statusDistribution = statusDist || {};
    const dataMap: Record<string, { value: number; label: string; color: string; bgColor: string }> = {
      completed: {
        value: stats.completed || statusDistribution.completed || 0,
        label: 'Completed',
        color: '#10b981',
        bgColor: 'rgba(16, 185, 129, 0.1)',
      },
      inprogress: {
        value: stats.in_progress || statusDistribution.in_progress || 0,
        label: 'In Progress',
        color: '#3b82f6',
        bgColor: 'rgba(59, 130, 246, 0.1)',
      },
      pending: {
        value: stats.pending || statusDistribution.pending || 0,
        label: 'Pending',
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.1)',
      },
      cancelled: {
        value: stats.cancelled || statusDistribution.cancelled || 0,
        label: 'Cancelled',
        color: '#ef4444',
        bgColor: 'rgba(239, 68, 68, 0.1)',
      },
    };

    return {
      first: dataMap[first] || dataMap.completed,
      second: dataMap[second] || dataMap.inprogress,
    };
  };

  // Initialize Task Trend Over Time Chart
  useEffect(() => {
    if (!performanceData || !chartRefs.taskTrendChartRef.current) return;

    if (taskTrendChartInstance.current) {
      taskTrendChartInstance.current.destroy();
    }

    const ctx = chartRefs.taskTrendChartRef.current.getContext('2d');
    if (!ctx) return;

    const hourlyLabels = Array.from({ length: 24 }, (_, i) => {
      return `${i.toString().padStart(2, '0')}:00`;
    });

    const { first, second } = getTaskData(
      selections.taskTrend,
      performanceData.statistics.tasks,
      performanceData.charts.status_distribution
    );
    
    // Use real task timeline data to create hourly distribution
    const taskTimeline = performanceData.task_timeline || [];
    const hourlyData: { [key: number]: { first: number; second: number } } = {};
    
    // Initialize hourly data
    for (let i = 0; i < 24; i++) {
      hourlyData[i] = { first: 0, second: 0 };
    }
    
    // Distribute tasks across hours based on completion time
    taskTimeline.forEach((task: any) => {
      if (task.completed_at) {
        const date = new Date(task.completed_at);
        const hour = date.getHours();
        const status = task.status;
        
        // Map status to first/second based on selection
        const [firstStatus, secondStatus] = selections.taskTrend.split('-');
        if (status === 'completed' || status === 'approved') {
          if (firstStatus === 'completed' || secondStatus === 'completed') {
            hourlyData[hour].first += 1;
          }
        } else if (status === 'in_progress') {
          if (firstStatus === 'inprogress' || secondStatus === 'inprogress') {
            hourlyData[hour].second += 1;
          }
        } else if (status === 'pending') {
          if (firstStatus === 'pending' || secondStatus === 'pending') {
            hourlyData[hour].second += 1;
          }
        }
      }
    });
    
    // If no timeline data, distribute evenly based on total counts
    const hasTimelineData = taskTimeline.length > 0;
    const firstData = hourlyLabels.map((_, index) => {
      if (hasTimelineData) {
        return hourlyData[index].first;
      }
      // Fallback: distribute evenly during work hours
      if (index >= 8 && index <= 17) {
        return Math.floor((first.value / 10) * 0.5);
      }
      return Math.floor((first.value / 20) * 0.2);
    });
    
    const secondData = hourlyLabels.map((_, index) => {
      if (hasTimelineData) {
        return hourlyData[index].second;
      }
      // Fallback: distribute evenly during work hours
      if (index >= 9 && index <= 18) {
        return Math.floor((second.value / 8) * 0.5);
      }
      return Math.floor((second.value / 15) * 0.2);
    });

    taskTrendChartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: hourlyLabels,
        datasets: [
          {
            label: first.label,
            data: firstData,
            borderColor: first.color,
            backgroundColor: first.bgColor,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: first.color,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 1.5,
            fill: true,
            tension: 0.4,
          },
          {
            label: second.label,
            data: secondData,
            borderColor: second.color,
            backgroundColor: second.bgColor,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: second.color,
            pointBorderColor: '#ffffff',
            pointBorderWidth: 1.5,
            fill: true,
            tension: 0.4,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            align: 'center',
            labels: {
              usePointStyle: true,
              pointStyle: 'rect',
              padding: 12,
              font: {
                size: 10,
                weight: 'bold'
              },
              color: '#374151'
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            cornerRadius: 8,
            padding: 10,
            titleFont: {
              size: 11
            },
            bodyFont: {
              size: 10
            },
          }
        },
        scales: {
          x: {
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.08)',
              lineWidth: 1,
            },
            ticks: {
              maxRotation: 45,
              minRotation: 0,
              font: {
                size: 10
              },
              color: '#6b7280'
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.08)',
              lineWidth: 1,
            },
            ticks: {
              stepSize: 20,
              font: {
                size: 10
              },
              color: '#6b7280'
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      },
    });
  }, [performanceData, chartRefs.taskTrendChartRef, selections.taskTrend]);

  // Initialize Task Ratio Chart
  useEffect(() => {
    if (!performanceData || !chartRefs.taskRatioChartRef.current) return;

    if (taskRatioChartInstance.current) {
      taskRatioChartInstance.current.destroy();
    }

    const ctx = chartRefs.taskRatioChartRef.current.getContext('2d');
    if (!ctx) return;

    const { first, second } = getTaskData(
      selections.taskRatio,
      performanceData.statistics.tasks,
      performanceData.charts.status_distribution
    );
    
    taskRatioChartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: [`${first.label} Tasks`, `${second.label} Tasks`],
        datasets: [{
          label: 'Task Count',
          data: [first.value, second.value],
          backgroundColor: [first.color, second.color],
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 60,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            cornerRadius: 8,
            padding: 10,
            titleFont: {
              size: 11
            },
            bodyFont: {
              size: 10
            },
            callbacks: {
              label: function(context: any) {
                return `${context.dataset.label}: ${context.parsed.y}`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.08)',
              lineWidth: 1,
            },
            ticks: {
              stepSize: 20,
              font: {
                size: 10
              },
              color: '#6b7280'
            }
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              font: {
                size: 10
              },
              color: '#6b7280'
            }
          }
        },
      },
    });
  }, [performanceData, chartRefs.taskRatioChartRef, selections.taskRatio]);

  // Initialize Points Over Time Chart
  useEffect(() => {
    if (!performanceData || !chartRefs.pointsChartRef.current) return;

    if (pointsChartInstance.current) {
      pointsChartInstance.current.destroy();
    }

    const ctx = chartRefs.pointsChartRef.current.getContext('2d');
    if (!ctx) return;

    const pointsData = performanceData.statistics.points.over_time || [];
    
    pointsChartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: pointsData.length > 0 ? pointsData.map(d => d.date) : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        datasets: [{
          label: 'Points Earned',
          data: pointsData.length > 0 ? pointsData.map(d => d.points) : [0, 0, 0, 0, 0, 0, 0],
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: '#3b82f6',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 1.5,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            cornerRadius: 8,
            padding: 10,
            titleFont: {
              size: 11
            },
            bodyFont: {
              size: 10
            },
          }
        },
        scales: {
          x: {
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.08)',
              lineWidth: 1,
            },
            ticks: {
              font: {
                size: 10
              },
              color: '#6b7280'
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.08)',
              lineWidth: 1,
            },
            ticks: {
              stepSize: 10,
              font: {
                size: 10
              },
              color: '#6b7280'
            },
          },
        },
      },
    });
  }, [performanceData, chartRefs.pointsChartRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (statusChartInstance.current) statusChartInstance.current.destroy();
      if (typeChartInstance.current) typeChartInstance.current.destroy();
      if (pointsChartInstance.current) pointsChartInstance.current.destroy();
      if (categoryChartInstance.current) categoryChartInstance.current.destroy();
      if (taskTrendChartInstance.current) taskTrendChartInstance.current.destroy();
      if (taskRatioChartInstance.current) taskRatioChartInstance.current.destroy();
    };
  }, []);

  return {
    statusChartInstance,
    typeChartInstance,
    pointsChartInstance,
    categoryChartInstance,
    taskTrendChartInstance,
    taskRatioChartInstance,
  };
};

